import { useEffect, useRef, useState } from 'react';
import { useStores } from '@root/src/stores';

/**
 * Map center that follows the chosen item's coords, but snaps to the region
 * center whenever the region (geo_center) changes — without touching the
 * current selection. "Last action wins": selecting an item centers on it;
 * toggling the region centers on the region.
 *
 * The consuming component must be an `observer` for the region change to
 * propagate (it reads `cartStore.regionData`).
 */
export const useRegionAwareCenter = (
  selectedCenter?: number[] | null
): number[] | undefined => {
  const { cartStore } = useStores();
  const regionCenter = cartStore?.regionData?.fields?.geo_center ?? undefined;

  const [center, setCenter] = useState<number[] | undefined>(
    selectedCenter?.length ? selectedCenter : regionCenter ?? undefined
  );

  // Selection wins: center on the chosen item when its coords change.
  const selectedKey = selectedCenter?.length ? selectedCenter.join(',') : '';
  useEffect(() => {
    if (selectedCenter?.length) {
      setCenter(selectedCenter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  // Region toggle wins: snap to the new region center (skip the initial mount).
  const regionKey = regionCenter?.join(',');
  const prevRegionKey = useRef(regionKey);
  useEffect(() => {
    if (regionKey && regionKey !== prevRegionKey.current) {
      setCenter(regionCenter);
    }
    prevRegionKey.current = regionKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionKey]);

  return center;
};

export default useRegionAwareCenter;
